<template>
  <div v-show="visibleSyslogInfo">
    <el-row type="flex" class="title-bar" justify="space-between">
      <div class="vertical-center-box">
        <span class="tit content-title">시스템 로그</span>
        <span class="condition-text">({{ logPeriodLabel }})</span>
        <span v-if="isTrue(syslogEventTimeData.isAbnormal)" class="abnormal-message">
          {{ syslogEventTimeData.abnormalMessage }}
        </span>
      </div>
      <w-header-search-button size="mini" @click="showSyslogSearchTool = !showSyslogSearchTool"></w-header-search-button>
    </el-row>
    <el-table-wrapper
      ref="syslogTable"
      :total="syslogTotal"
      :data="syslogTableData"
      :columns="syslogColumns"
      :show-custom-header="true"
      :show-search-tool="showSyslogSearchTool"
      remote
      :pagination="true"
      :column-default="columnDefault"
      @condition-change="loadData"
    >
      <template slot-scope="scope" slot="is-checked-slot">
        <span v-if="scope.row.isChecked === '1'">확인</span>
        <span v-else-if="scope.row.isChecked === '0'">미확인</span>
        <span v-else>-</span>
      </template>
    </el-table-wrapper>
  </div>
</template>

<script>
import { isNil, isEmpty, forEach, findIndex } from 'lodash';
import { isTrue } from '@/utils/boolean';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { columnDefault } from '@/constants/table';
import syslogAPI from '@/api/log/common';
import { getDateToString, getIntervalDatetimeParams } from '@/utils/date';
import { etcInfoDefine } from '../../../../constants/fault-analysis';
import { getLogPeriodCondition, getPeriodConditionLabel } from '../../../../utils/fault-analysis';

export default {
  name: 'WSyslogTable',
  props: {
    /**
     * 기타 분석 정보 조회 시 필요한 정보 객체 (동시성 문제 고려하여 하나의 객체로 전달)
     * 1) obj: 분석 대상 장비 정보
     * 2) eventHistData: 분석 이벤트 이력 정보
     * 3) etcInfo: 기타 분석 정보
     * @type {Object}
     */
    data: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      columnDefault,
      showSyslogSearchTool: false,
      syslogTableData: [],
      syslogTotal: 0,
      syslogEventTimeData: {},
      syslogLoaded: false,
      visibleSyslogInfo: false,
    };
  },
  computed: {
    isLms() {
      return findIndex(this.$store.getters.activeManagetypeIds, (item) => item === 'LMS') >= 0;
    },
    syslogColumns() {
      if (!this.isLms) {
        return [
          {
            prop: 'severity',
            label: 'severity',
            showOverflowTooltip: true,
            width: 100,
            searchable: true,
            formatter: this.getTableValueAfterCheckEmpty,
          },
          {
            prop: 'filterName',
            label: '필터',
            width: 150,
            showOverflowTooltip: true,
            searchable: true,
            formatter: this.getTableValueAfterCheckEmpty,
          },
          {
            prop: 'message',
            label: '메시지',
            showOverflowTooltip: true,
            searchable: true,
            formatter: this.getTableValueAfterCheckEmpty,
          },
          {
            prop: '@timestamp',
            label: '수집시각',
            showOverflowTooltip: true,
            formatter: (row, column, value) => this.getDateFormatter(value),
            width: 200,
          },
        ];
      }
      return [
        {
          //   type: 'selection',
          //   width: '50',
          // }, {
          prop: 'isChecked',
          label: '상태',
          width: 60,
          scopedSlot: 'is-checked-slot',
        },
        {
          prop: 'sourceName',
          label: '로그 소스',
          width: 150,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'originMessage',
          label: '원본 메시지',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'filterName',
          label: '정책 이름',
          width: 150,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'timestamp',
          label: '수집 시간',
          width: 160,
          searchable: true,
          searchType: 'date',
          formatter: (row, column, value) => this.getDateFormatter(value),
        },
      ];
    },
    logPeriodLabel() {
      if (isEmpty(this.data) || isEmpty(this.data.eventHistData)) {
        return EMPTY_TEXT;
      }
      const period = getLogPeriodCondition(this.data.eventHistData.checkDatetime);
      return getPeriodConditionLabel(period.startDatetime, period.endDatetime);
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(to) {
        this.reset();
        if (!isNil(to)) {
          this.loadData();
        }
      },
    },
  },
  methods: {
    isTrue,
    /**
     * [시스템 로그] 데이터 조회
     * @author jeee12
     */
    loadData() {
      if (!isEmpty(this.data) && !isEmpty(this.data.etcInfo) && !isEmpty(this.data.etcInfo[etcInfoDefine.NETDEVICE_SYSLOG])) {
        this.syslogEventTimeData = this.data.etcInfo[etcInfoDefine.NETDEVICE_SYSLOG];
      }
      if (!isEmpty(this.data.eventHistData)) {
        this.$nextTick(() => {
          const periodCondition = getLogPeriodCondition(this.data.eventHistData.checkDatetime);
          const tableSearchParams = this.$refs.syslogTable.getParameter();
          const params = {
            objId: this.data.obj.objId,
            logType: 'SYSLOG',
            type: 'performance',
            startDateTime: periodCondition.startDatetime,
            endDateTime: periodCondition.endDatetime,
            start: tableSearchParams.start,
            limit: tableSearchParams.limit,
          };

          const queryList = [];
          if (!isNil(tableSearchParams.searchParams)) {
            forEach(tableSearchParams.searchParams, (item) => {
              if (item.field === 'isChecked') {
                queryList.push({
                  field: item.field,
                  value: item.value === '확인' ? '1' : '0',
                  operator: 'AND',
                });
              } else {
                queryList.push({
                  field: item.field,
                  value: item.value,
                  operator: 'AND',
                });
              }
            });
          }
          if (this.isLms) {
            queryList.push({
              field: 'objId',
              value: this.data.obj.objId,
              operator: 'AND',
              regex: 'N',
            });
            queryList.push({
              field: 'logType',
              value: 'SYSLOG',
              operator: 'AND',
              regex: 'N',
            });
          }
          params.queryList = queryList;
          syslogAPI
            .getLogAnalysisData(params)
            .then((response) => {
              if (response.data.success) {
                this.syslogTableData = response.data.data;
                this.syslogTotal = response.data.total;
              }
            })
            .catch((e) => {
              throw new Error(e);
            })
            .finally(() => {
              if (!this.syslogLoaded) {
                this.syslogLoaded = true;
                this.visibleSyslogInfo = this.syslogTotal > 0;
              }
            });
        });
      }
    },
    /**
     * 화면을 초기화한다.
     * @author Ju
     */
    reset() {
      this.syslogTableData = [];
      this.syslogTotal = 0;
      this.syslogEventTimeData = {};
      this.syslogLoaded = false;
      this.visibleSyslogInfo = false;
    },
    /**
     * 테이블 공백 값 처리
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    getTableValueAfterCheckEmpty(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    /**
     * [공통] 날짜 포맷터
     * @author jeee12
     * @param  {[type]} value    [description]
     * @param  {[type]} dataType 데이터 종류 (syslog테이블에서 LMS가 활성화된 경우, 다른 포매터 수행)
     * @return {[type]}          [description]
     */
    getDateFormatter(value) {
      if (this.isLms) {
        if (!isNil(value)) {
          return getDateToString(getIntervalDatetimeParams(value, 'hour', 9), 'YYYY-MM-DD HH:mm:ss');
        }
      }
      return isNil(value) ? EMPTY_TEXT : getDateToString(value);
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/popup/FaultAnalysisPopup/components/FaultAnalysisInfoView/EtcInfo/NetdeviceEtcInfoView/SyslogTable.vue